import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ContextMenu, Menu } from '@syncfusion/ej2-navigations';
import { ColumnMenu } from '@syncfusion/ej2-grids';

/** Minimum gap kept between a positioned popup and the viewport edge. */
const MENU_VIEWPORT_MARGIN = 4;

/**
 * Where a popup should sit relative to the element it was opened from, in PHYSICAL
 * viewport px.
 *
 * Shared by the shims below so the boundary behaviour is defined once: a popup opens
 * `beside` its anchor (submenus, the grid's filter popup) or `below` it (top-level menus),
 * flips to the anchor's other side when the viewport leaves no room, and is pulled back
 * inside rather than allowed to overflow.
 *
 * Each caller converts the result into whatever coordinate space its own popup is
 * positioned in. That conversion differs — document-relative for menu wrappers, containing
 * block-relative for the filter popup — and is deliberately NOT part of this helper.
 *
 * Pure: it reads only the rects passed in, so it can be reasoned about, and compared
 * against the arithmetic it replaced, without a DOM.
 * @param anchor - The anchor element's rect.
 * @param popup - The popup's own rect, for its measured width and height.
 * @param placement - 'beside' opens to the anchor's right, 'below' opens under it.
 * @param win - Window supplying the viewport bounds.
 * @returns The popup's physical viewport left/top.
 */
function anchoredPopupPosition(
  anchor: DOMRect,
  popup: DOMRect,
  placement: 'beside' | 'below',
  win: Window,
): { left: number; top: number } {
  const margin = MENU_VIEWPORT_MARGIN;
  let left: number;
  let top: number;
  if (placement === 'beside') {
    left = anchor.right;
    if (left + popup.width > win.innerWidth - margin) {
      left = Math.max(margin, anchor.left - popup.width);
    }
    top = anchor.top;
    if (top + popup.height > win.innerHeight - margin) {
      top = Math.max(margin, win.innerHeight - popup.height - margin);
    }
  } else {
    left = anchor.left;
    if (left + popup.width > win.innerWidth - margin) {
      left = Math.max(margin, anchor.right - popup.width);
    }
    top = anchor.bottom;
    if (top + popup.height > win.innerHeight - margin) {
      top = Math.max(margin, anchor.top - popup.height);
    }
  }
  return { left, top };
}

/**
 * Installs one-time shims over Syncfusion's menu positioning so menus land in the right
 * place under the CSS `zoom` the shell applies to `<body>` (see {@link BodyZoomService}).
 *
 * Two different Syncfusion pipelines need correcting, each patched by a focused helper:
 * - {@link patchContextMenuPositioning} — grid column menus, submenus and right-click grid
 *   context menus (`ContextMenu.prototype.setPosition`).
 * - {@link patchMenuPositioning} — `ejs-menu` popups such as the navbar profile menu
 *   (`Menu.prototype.collision`).
 * - {@link patchColumnMenuFilterPositioning} — the grid's Excel filter popup, opened from a
 *   column menu's "Filter" item (`ej2-grids` `ColumnMenu.prototype.setPosition`). Unlike
 *   the other two this one re-anchors outright rather than scaling, and so is not a no-op
 *   at zoom 1 — see its own note.
 *
 * The two scaling shims are no-ops at zoom 1, so behaviour is unchanged on viewports where no body
 * zoom is applied. Remove once Syncfusion handles body zoom in menu positioning natively.
 */
@Injectable({ providedIn: 'root' })
export class SyncfusionMenuZoomService {

  /** Guard so the prototype patches are installed exactly once per page load — static so
   * even a second service instance (e.g. in tests) cannot double-wrap the prototypes. */
  private static installed = false;

  /**
   * @param document - Injected document; also the platform-safe route to the window object
   * used for viewport measurements inside the shims.
   */
  constructor(@Inject(DOCUMENT) private document: Document) {}

  /**
   * Installs both menu-positioning shims. Safe to call repeatedly — only the first call
   * patches the prototypes.
   * @returns Nothing.
   */
  install(): void {
    if (SyncfusionMenuZoomService.installed) {
      return;
    }
    SyncfusionMenuZoomService.installed = true;
    this.patchContextMenuPositioning();
    this.patchMenuPositioning();
    this.patchColumnMenuFilterPositioning();
  }

  /**
   * Reads the effective CSS zoom currently applied to `<body>`.
   * @returns The zoom factor, `1` when none is applied.
   */
  private getBodyZoom(): number {
    const win = this.document.defaultView!;
    return parseFloat(win.getComputedStyle(this.document.body).zoom as string) || 1;
  }

  /**
   * Corrects `ContextMenu` coordinates for the body zoom.
   *
   * Root cause: the grid column menu (`ej2-grids` ColumnMenu → `ContextMenu.open(top, left)`)
   * computes its position from `getBoundingClientRect()` — i.e. in *unzoomed* root pixels —
   * and Syncfusion's menu pipeline (`ej2-navigations` menu-base `setPosition`) applies them
   * as inline `top`/`left` on an element inside the zoomed `<body>`, where they render
   * multiplied by the zoom factor. Under the app's 0.8 body zoom every grid column menu
   * therefore opened up-left of its header cell. (`ej2-popups`' `fit()` already divides by
   * the body zoom — see its `collision.js` `getZoomValue` — the menu code path lacks the
   * same compensation.)
   *
   * The correction is applied AFTER the original `setPosition` has run, not by pre-scaling
   * the coordinates passed to `open()`: `setPosition` performs viewport collision checks
   * (`isCollide`) in unzoomed pixels, so feeding it pre-scaled values makes right-edge
   * columns falsely register a "right" collision and the menu is shifted a full menu width
   * to the left. Letting Syncfusion finish all of its (self-consistent, unzoomed) math and
   * then dividing only the final inline `top`/`left` keeps every collision decision intact.
   * `setPosition` is also the single write point for submenus ("Columns ▸") and right-click
   * grid context menus, so all of them are corrected by this one patch.
   * @returns Nothing.
   */
  private patchContextMenuPositioning(): void {
    const getZoom = () => this.getBodyZoom();
    // setPosition is protected in Syncfusion's typings; the cast is required to wrap it.
    const proto = ContextMenu.prototype as unknown as {
      setPosition(li: HTMLElement | null, ul: HTMLElement, top: number, left: number, isOpen?: boolean): void;
    };
    const originalSetPosition = proto.setPosition;
    proto.setPosition = function (li: HTMLElement | null, ul: HTMLElement, top: number, left: number, isOpen?: boolean): void {
      originalSetPosition.call(this, li, ul, top, left, isOpen);
      const zoom = getZoom();
      if (zoom === 1) {
        return;
      }
      // The original assigns the final coordinates either to the menu <ul> itself or, when
      // menu scrolling is enabled, to its .e-menu-vscroll wrapper — correct whichever
      // element carries the inline position. Each open recomputes fresh unzoomed values
      // before this runs, so the division is applied exactly once per positioning pass.
      const scrollWrapper = ul.closest<HTMLElement>('.e-menu-vscroll');
      const positioned = scrollWrapper && scrollWrapper.style.left ? scrollWrapper : ul;
      const finalLeft = parseFloat(positioned.style.left);
      const finalTop = parseFloat(positioned.style.top);
      if (!Number.isNaN(finalLeft)) {
        positioned.style.left = `${finalLeft / zoom}px`;
      }
      if (!Number.isNaN(finalTop)) {
        positioned.style.top = `${finalTop / zoom}px`;
      }
    };
  }

  /**
   * Re-anchors `Menu` (ejs-menu — e.g. the profile/username menu in the navbar) popups
   * under the body zoom.
   *
   * The Menu component takes a different path than ContextMenu: its popup is placed
   * by Popup.refreshPosition + MenuBase.collision(), which mix zoomed and unzoomed
   * coordinate spaces under a body zoom (runtime-measured: horizontal ~correct,
   * vertical drifts by (1 − zoom) of the anchor offset, and right-edge popups can
   * overflow the viewport). collision() is the LAST writer of the popup position in
   * every branch (for templated menus it runs in a requestAnimationFrame after the
   * other writes), so re-anchoring here deterministically wins. The popup is placed
   * directly from the anchor <li>'s physical rect — below the anchor for top-level
   * menus, beside it for nested submenus — then converted into the zoomed document
   * coordinate space. No-ops at zoom 1.
   * @returns Nothing.
   */
  private patchMenuPositioning(): void {
    const getZoom = () => this.getBodyZoom();
    const win = this.document.defaultView!;
    // Menu's positioning internals are not part of Syncfusion's public typings; the cast exposes them.
    const menuProto = Menu.prototype as unknown as {
      collision(): void;
      lItem?: HTMLElement;
      popupWrapper?: HTMLElement;
      isNestedOrVertical?: boolean;
      left?: number;
      top?: number;
    };
    const originalCollision = menuProto.collision;
    menuProto.collision = function (this: typeof menuProto): void {
      originalCollision.call(this);
      const zoom = getZoom();
      const anchor = this.lItem;
      const wrap = this.popupWrapper;
      if (zoom === 1 || !anchor || !wrap) {
        return;
      }
      const a = anchor.getBoundingClientRect();
      const r = wrap.getBoundingClientRect();
      // Submenus open beside their parent item, top-level menus below their anchor.
      const { left, top } = anchoredPopupPosition(a, r, this.isNestedOrVertical ? 'beside' : 'below', win);
      // Physical viewport px → zoomed document px (the wrapper is absolutely
      // positioned in the zoomed <body>).
      this.left = (left + win.scrollX) / zoom;
      this.top = (top + win.scrollY) / zoom;
      wrap.style.left = `${this.left}px`;
      wrap.style.top = `${this.top}px`;
    };
  }

  /**
   * Re-anchors the grid's Excel filter popup beside the column menu it was opened from.
   *
   * The popup is placed by `ej2-grids`' `ColumnMenu.prototype.setPosition` — a different
   * function from the `ContextMenu` one patched above, which is why the column menu and its
   * "Columns ▸" submenu land correctly while this one does not. Two independent offsets go
   * wrong there, which is why correcting the arithmetic is not enough:
   *
   * 1. It derives the offset from `getBoundingClientRect()` (physical, zoom-scaled px) and
   *    writes it as an inline `left`/`top` inside the zoomed `<body>`, where CSS px render
   *    multiplied by the zoom — so the popup lands at `offset * zoom`, short of the spot
   *    Syncfusion's own math intends (it adds the menu's width precisely so the popup clears
   *    it).
   * 2. When the grid's content is SHORTER than the popup, `resetDialogAppend`
   *    (excel-filter-base.js) moves the popup out of the grid into a `.e-grid-popup` div on
   *    `<body>`, positioned from its own uncompensated `calculatePosition(grid)`. The
   *    popup's offsets — computed relative to the GRID — are then applied inside that
   *    container instead. Two stacked, independently wrong offsets, and which one applies
   *    depends on nothing more than the grid's row count.
   *
   * So rather than correcting either, this places the popup outright from the column menu's
   * measured rect and converts into the popup's OWN containing block. That is the same
   * approach {@link patchMenuPositioning} takes, and it is indifferent to both problems: it
   * never reads Syncfusion's offsets, and the containing block is measured wherever the
   * popup actually ended up. Runs at every zoom, so a short grid and a tall one behave
   * identically.
   *
   * `setPosition` has exactly one caller — `filterPosition()`, which runs only when the
   * popup was opened from a column menu — so nothing else is affected. Syncfusion's own
   * result is left in place if the elements cannot be measured.
   * @returns Nothing.
   */
  private patchColumnMenuFilterPositioning(): void {
    const getZoom = () => this.getBodyZoom();
    const win = this.document.defaultView!;
    // setPosition is internal to the grid's ColumnMenu module; the cast is required to wrap it.
    const proto = ColumnMenu.prototype as unknown as {
      setPosition(li: HTMLElement, ul: HTMLElement): void;
    };
    const originalSetPosition = proto.setPosition;
    proto.setPosition = function (li: HTMLElement, ul: HTMLElement): void {
      // Keep Syncfusion's placement as the fallback for the bail-outs below.
      originalSetPosition.call(this, li, ul);

      // `li` is the column menu's <ul> (filterPosition passes the Filter item's parent).
      const menu = li.getBoundingClientRect();
      const pop = ul.getBoundingClientRect();
      // offsetParent is the grid element, or the .e-grid-popup container after relocation.
      const host = ul.offsetParent as HTMLElement | null;
      if (!host || !menu.width || !pop.width) {
        return;
      }
      const zoom = getZoom();
      const hostRect = host.getBoundingClientRect();
      // Beside the column menu, on the same boundary rules as every other popup here.
      const { left, top } = anchoredPopupPosition(menu, pop, 'beside', win);

      // Physical viewport px → CSS px inside the popup's containing block, which is the
      // host's PADDING box — hence clientLeft/clientTop, so a bordered host does not shift
      // the result by its border width.
      const originX = hostRect.left + host.clientLeft * zoom;
      const originY = hostRect.top + host.clientTop * zoom;
      ul.style.left = `${(left - originX) / zoom}px`;
      ul.style.top = `${(top - originY) / zoom}px`;
    };
  }
}
